import React from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      {/* Hero Section */}
      <section className="legal-hero">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="legal-subtitle">
            Your privacy is important to us. Learn how we collect, use, and
            protect your information.
          </p>
          <div className="legal-meta">
            <span>
              <strong>Effective Date:</strong> January 1, 2025
            </span>
            <span>
              <strong>Last Updated:</strong> December 29, 2025
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content">
        <div className="container">
          <div className="legal-intro">
            <p>
              At GoalFlex, we are committed to protecting your privacy and
              ensuring transparency about how we handle your personal
              information. We collect minimal data necessary for app functionality
              and never sell your personal information. This Privacy Policy explains our practices regarding
              the collection, use, and disclosure of information we receive when
              you use our platform.
            </p>
          </div>

          <div className="legal-section">
            <h2>1. Information We Collect</h2>
            <div className="subsection">
              <h3>Account Information</h3>
              <ul>
                <li>Name, email address, and phone number</li>
                <li>Profile photo and bio (optional)</li>
                <li>Skill level and position preferences</li>
                <li>Password and authentication credentials</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Game and Activity Data</h3>
              <ul>
                <li>Game bookings and participation history</li>
                <li>Player ratings and reviews</li>
                <li>Communication within the platform</li>
                <li>Payment and transaction information</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Technical Information</h3>
              <ul>
                <li>
                  Device information (IP address, browser type, operating
                  system)
                </li>
                <li>Usage analytics and app performance data</li>
                <li>Location data (with your permission)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>
                <strong>Platform Operation:</strong> Create and manage your
                account, facilitate game bookings
              </li>
              <li>
                <strong>Matching Services:</strong> Connect you with suitable
                players and games
              </li>
              <li>
                <strong>Communication:</strong> Send important updates,
                confirmations, and notifications
              </li>
              <li>
                <strong>Safety & Security:</strong> Verify identity, prevent
                fraud, and ensure platform safety
              </li>
              <li>
                <strong>Improvement:</strong> Analyze usage patterns to enhance
                our services
              </li>
              <li>
                <strong>Marketing:</strong> Send promotional content (with your
                consent)
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. Information Sharing and Disclosure</h2>
            <p>
              We do not sell your personal information. We may share your
              information only in these circumstances:
            </p>
            <ul>
              <li>
                <strong>With Other Players:</strong> Basic profile information
                for game organization
              </li>
              <li>
                <strong>Service Providers:</strong> Third-party services that
                help us operate (hosting, payment processing)
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to
                protect rights and safety
              </li>
              <li>
                <strong>Business Transfers:</strong> In case of merger,
                acquisition, or asset sale
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your
              information, including encryption, secure servers, and regular
              security audits. However, no method of transmission over the
              internet is 100% secure. We encourage you to use strong passwords
              and enable two-factor authentication when available.
            </p>
          </div>

          <div className="legal-section">
            <h2>5. Your Rights and Choices</h2>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of your personal
                information
              </li>
              <li>
                <strong>Correction:</strong> Update or correct inaccurate
                information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your account and
                data
              </li>
              <li>
                <strong>Portability:</strong> Export your data in a
                machine-readable format
              </li>
              <li>
                <strong>Opt-out:</strong> Unsubscribe from marketing
                communications
              </li>
              <li>
                <strong>Location:</strong> Disable location tracking in your
                device settings
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your information as follows:
            </p>
            <ul>
              <li>
                <strong>Active Accounts:</strong> Information is retained while your account remains active
              </li>
              <li>
                <strong>Inactive Accounts:</strong> Accounts inactive for 2 years will be automatically deleted
              </li>
              <li>
                <strong>Financial Records:</strong> Payment and transaction records are retained for 7 years for legal compliance
              </li>
              <li>
                <strong>Game History:</strong> May be retained longer for platform integrity, but will be anonymized where possible
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>7. International Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries
              other than your own. We ensure appropriate safeguards are in place
              to protect your information in accordance with this Privacy
              Policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              Our services are not intended for children under 16. We do not
              knowingly collect personal information from children under 16. If
              you believe we have collected information from a child under 16,
              please contact us immediately.
            </p>
          </div>

          <div className="legal-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of material changes by email or through the platform.
              Your continued use of our services after such notification
              constitutes acceptance of the updated policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>10. Contact Information</h2>
            <p>
              If you have questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="contact-info">
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:privacy@goalflex.net">privacy@goalflex.net</a>
              </p>
              <p>
                <strong>Support:</strong>{" "}
                <a href="mailto:support@goalflex.net">support@goalflex.net</a>
              </p>
              <p>
                <strong>Address:</strong> GoalFlex, Inc.
                <br />
                123 Soccer Street
                <br />
                San Francisco, CA 94105
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
